import { Injectable } from '@angular/core';
import { HttpHeaders, HttpParams } from '@angular/common/http';
import { Observable } from 'rxjs';
import { jwtDecode } from 'jwt-decode';
import { CookieService } from 'ngx-cookie-service';
import { HttpClient } from '@angular/common/http';
import { Router } from '@angular/router';
import { ApiBaseService } from '../base/api-base.service';
import { User } from 'src/app/shared/interfaces/dashboard.models';

interface JwtPayload {
  sub: string;
  scope: string;
  exp: number;
  userId: string;
  firstName?: string;
  lastName?: string;
  roles: string[];
}

@Injectable({ providedIn: 'root' })
export class AuthService extends ApiBaseService {
  private readonly AUTH_URL = this.apiUrl.replace('v1/', '');
  private readonly TOKEN_KEY = 'access-token';

  constructor(
    http: HttpClient,
    private cookieService: CookieService,
    private router: Router, // ← injection explicite ici
  ) {
    super(http);
  }

  login(email: string, password: string): Observable<any> {
    const headers = new HttpHeaders({
      'Content-Type': 'application/x-www-form-urlencoded',
    });
    const body = new HttpParams().set('email', email).set('password', password);
    return this.http.post<any>(this.AUTH_URL + 'auth/login', body, { headers });
  }

  loadUserProfile(data: { accessToken: string }): void {
    this.setToken(data['accessToken']);
  }

  logout(): void {
    this.http.post(this.AUTH_URL + 'auth/logout', {}).subscribe({
      next: () => this.clearSession(),
      error: () => this.clearSession(),
    });
  }

  private clearSession(): void {
    this.removeToken();
    this.router.navigate(['/login']);
  }

  getCurrentUser(): User | null {
    const token = this.getToken();
    if (!token) return null;
    try {
      const decoded = jwtDecode<JwtPayload>(token);
      const firstName = decoded.firstName ?? decoded.sub.split('@')[0];
      const lastName = decoded.lastName ?? '';
      return {
        id: decoded.userId,
        email: decoded.sub,
        username: decoded.sub,
        firstName,
        lastName,
        initials: this.buildInitials(firstName, lastName, decoded.sub),
        roles: decoded.roles ?? [],
      };
    } catch {
      return null;
    }
  }

  isAuthenticated(): boolean {
    const token = this.getToken();
    if (!token) return false;
    try {
      const { exp } = jwtDecode<JwtPayload>(token);
      return Date.now() < exp * 1000;
    } catch {
      return false;
    }
  }

  hasRole(role: string): boolean {
    return this.getCurrentUser()?.roles.some((r) => r.includes(role)) ?? false;
  }

  getToken(): string {
    return this.cookieService.get(this.TOKEN_KEY);
  }

  setToken(token: string): void {
    this.cookieService.set(this.TOKEN_KEY, token);
  }

  removeToken(): void {
    this.cookieService.delete(this.TOKEN_KEY);
  }

  private buildInitials(
    firstName: string,
    lastName: string,
    email: string,
  ): string {
    if (firstName && lastName) {
      return (firstName.charAt(0) + lastName.charAt(0)).toUpperCase();
    }
    return email.substring(0, 2).toUpperCase();
  }

  public sendPasswordResetEmail(email: string): Observable<any> {
    const params = new HttpParams().set('email', email);
    return this.http.post(
      `${this.AUTH_URL}auth/forgot-password`,
      {},
      {
        params,
        responseType: 'text',
      },
    );
  }

  public resetPassword(token: string, password: string): Observable<any> {
    return this.http.post(
      `${this.AUTH_URL}auth/reset-password`,
      { token, password },
      { responseType: 'text' },
    );
  }
}
